import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Search, User as UserIcon, Phone, MapPin, ChevronLeft, ChevronRight,
  FileText, Calendar, Phone as PhoneIcon, Paperclip, Loader, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { RecordingPlayer } from './RecordingPlayer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const safeDate = (v) => {
  if (!v) return '';
  try {
    return new Date(v).toLocaleString();
  } catch (e) {
    return String(v);
  }
};

const StatPill = ({ icon: Icon, value, label, color = 'bg-blue-50 text-[#1565c0]' }) => (
  <div className={`flex items-center gap-2 rounded-xl px-3 py-2 ${color}`}>
    <Icon size={16} />
    <span className="font-extrabold text-base">{value}</span>
    <span className="text-xs">{label}</span>
  </div>
);

export const AdminPatientsView = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [bundleLoading, setBundleLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/users?role=patient`, { withCredentials: true });
        setPatients(r.data || []);
      } catch (e) {
        toast.error(t('فشل تحميل المرضى', 'Failed to load patients', 'Échec du chargement'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [t]);

  // Load the per-patient bundle when a patient is selected
  useEffect(() => {
    if (!selectedId) { setBundle(null); return; }
    let cancelled = false;
    const load = async () => {
      setBundleLoading(true);
      try {
        const r = await axios.get(`${API}/admin/patients/${selectedId}`, { withCredentials: true });
        if (!cancelled) setBundle(r.data);
      } catch (e) {
        if (!cancelled) toast.error(t('فشل تحميل بيانات المريض', 'Failed to load patient details', 'Échec'));
      } finally {
        if (!cancelled) setBundleLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedId, t]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter((p) => {
      const hay = `${p.full_name || ''} ${p.email || ''} ${p.phone_number || ''} ${p.location || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [patients, search]);

  const Chevron = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="space-y-4" data-testid="admin-patients-view">
      {/* Search + summary */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={16} className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} text-gray-400`} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('ابحث بالاسم، البريد، أو الهاتف...', 'Search by name, email, or phone...', 'Rechercher...')}
              className={isRTL ? 'pr-9' : 'pl-9'}
              data-testid="patients-search-input"
            />
          </div>
          <div className="text-sm text-gray-500">
            {filtered.length} / {patients.length} {t('مريض', 'patients', 'patients')}
          </div>
        </div>
      </Card>

      {/* List */}
      {loading && <Card className="p-8 text-center text-sm text-gray-500">{t('جاري التحميل...', 'Loading...', 'Chargement...')}</Card>}

      {!loading && filtered.length === 0 && (
        <Card className="p-8 text-center" data-testid="patients-empty">
          <UserIcon className="mx-auto text-gray-300 mb-3" size={48} />
          <p className="text-gray-500 text-sm">{t('لا توجد نتائج', 'No matching patients', 'Aucun résultat')}</p>
        </Card>
      )}

      <div className="grid gap-3">
        {!loading && filtered.map((p) => (
          <button
            key={p._id}
            type="button"
            onClick={() => setSelectedId(p._id)}
            data-testid={`patient-row-${p._id}`}
            className={`w-full text-start bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-4 hover:shadow-md hover:-translate-y-px transition ${isRTL ? 'flex-row-reverse text-right' : ''}`}
          >
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center overflow-hidden flex-shrink-0">
              {(p.profile_picture || p.profile_photo) ? (
                <img src={p.profile_picture || p.profile_photo} alt={p.full_name} className="w-full h-full object-cover" />
              ) : (
                <UserIcon size={26} className="text-[#1565c0]" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-[#0d47a1] truncate">{p.full_name}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-500 mt-0.5">
                {p.email && !p.email.endsWith('@tababa.local') && <span className="inline-flex items-center gap-1"><Mail size={12} />{p.email}</span>}
                {p.phone_number && <span className="inline-flex items-center gap-1"><Phone size={12} />{p.phone_number}</span>}
                {p.location && <span className="inline-flex items-center gap-1"><MapPin size={12} />{p.location}</span>}
              </div>
            </div>
            <Chevron size={18} className="text-gray-400 flex-shrink-0" />
          </button>
        ))}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!selectedId} onOpenChange={(open) => !open && setSelectedId(null)}>
        <SheetContent
          side={isRTL ? 'left' : 'right'}
          className={`${isRTL ? 'rtl' : 'ltr'} w-full sm:max-w-2xl overflow-y-auto`}
          data-testid="patient-detail-sheet"
        >
          <SheetHeader>
            <SheetTitle className="text-[#0d47a1]">
              {t('بيانات المريض', 'Patient Details', 'Détails du patient')}
            </SheetTitle>
          </SheetHeader>

          {bundleLoading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader className="animate-spin me-2" size={18} /> {t('جاري التحميل...', 'Loading...', 'Chargement...')}
            </div>
          )}

          {!bundleLoading && bundle && (
            <div className="space-y-4 mt-4">
              {/* Profile card */}
              <Card className="p-4">
                <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
                  <div className="w-20 h-20 rounded-full bg-blue-50 border-4 border-white shadow-md flex items-center justify-center overflow-hidden flex-shrink-0">
                    {(bundle.patient.profile_picture || bundle.patient.profile_photo) ? (
                      <img src={bundle.patient.profile_picture || bundle.patient.profile_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <UserIcon size={44} className="text-[#1565c0]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl font-extrabold text-[#0d47a1]" data-testid="patient-detail-name">{bundle.patient.full_name}</h2>
                    <div className="text-xs text-gray-500 space-y-0.5 mt-1">
                      {bundle.patient.email && !bundle.patient.email.endsWith('@tababa.local') && <div className="flex items-center gap-1"><Mail size={12} /> {bundle.patient.email}</div>}
                      {bundle.patient.phone_number && <div className="flex items-center gap-1"><Phone size={12} /> {bundle.patient.phone_number}</div>}
                      {bundle.patient.location && <div className="flex items-center gap-1"><MapPin size={12} /> {bundle.patient.location}</div>}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4" data-testid="patient-stats">
                  <StatPill icon={FileText} value={bundle.stats.notes} label={t('ملاحظات', 'Notes', 'Notes')} />
                  <StatPill icon={Calendar} value={bundle.stats.appointments} label={t('مواعيد', 'Appointments', 'RDV')} />
                  <StatPill icon={PhoneIcon} value={bundle.stats.calls} label={t('مكالمات', 'Calls', 'Appels')} color="bg-green-50 text-green-700" />
                  <StatPill icon={Paperclip} value={bundle.stats.files} label={t('ملفات', 'Files', 'Fichiers')} color="bg-purple-50 text-purple-700" />
                </div>
              </Card>

              {/* Tabs */}
              <Tabs defaultValue="notes" className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="notes" data-testid="patient-tab-notes">{t('الملاحظات', 'Notes', 'Notes')}</TabsTrigger>
                  <TabsTrigger value="calls" data-testid="patient-tab-calls">{t('المكالمات', 'Calls', 'Appels')}</TabsTrigger>
                  <TabsTrigger value="appointments" data-testid="patient-tab-appointments">{t('المواعيد', 'Apts', 'RDV')}</TabsTrigger>
                  <TabsTrigger value="files" data-testid="patient-tab-files">{t('الملفات', 'Files', 'Fichiers')}</TabsTrigger>
                </TabsList>

                {/* Notes */}
                <TabsContent value="notes" className="space-y-3 mt-3">
                  {bundle.notes.length === 0 ? (
                    <Card className="p-6 text-center text-sm text-gray-500">{t('لا توجد ملاحظات', 'No notes', 'Aucune note')}</Card>
                  ) : (
                    bundle.notes.map((n) => (
                      <Card key={n._id} className="p-4" data-testid={`patient-note-${n._id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-semibold text-[#0d47a1] text-sm">{n.author_name || '—'}</p>
                          <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500">{n.author_role}</span>
                        </div>
                        <p className="text-xs text-gray-400 mb-2">{safeDate(n.created_at)}</p>
                        {n.medications && <p className="text-sm"><b>{t('الأدوية:', 'Meds:', 'Méds:')}</b> {n.medications}</p>}
                        {n.dosage && <p className="text-sm"><b>{t('الجرعة:', 'Dosage:', 'Posologie:')}</b> {n.dosage}</p>}
                        <p className="text-sm text-gray-700 whitespace-pre-wrap mt-1">{n.notes}</p>
                      </Card>
                    ))
                  )}
                </TabsContent>

                {/* Calls */}
                <TabsContent value="calls" className="space-y-3 mt-3">
                  {bundle.calls.length === 0 ? (
                    <Card className="p-6 text-center text-sm text-gray-500">{t('لا توجد مكالمات', 'No calls', 'Aucun appel')}</Card>
                  ) : (
                    bundle.calls.map((c) => (
                      <Card key={c._id} className="p-4" data-testid={`patient-call-${c._id}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-semibold text-[#0d47a1]">{c.doctor_name || '—'}</span>
                          <span className={`text-[10px] uppercase font-bold tracking-wide px-2 py-0.5 rounded-full ${
                            c.status === 'ended' ? 'bg-gray-100 text-gray-600' :
                            c.status === 'active' ? 'bg-green-100 text-green-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {c.call_type} · {c.status}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">{safeDate(c.created_at)}</p>
                        {c.medical_form?.current_symptoms && (
                          <p className="text-sm text-gray-700 mt-2"><b>{t('الأعراض:', 'Symptoms:', 'Symptômes:')}</b> {c.medical_form.current_symptoms}</p>
                        )}
                        {c.medical_form?.additional_notes && (
                          <p className="text-sm text-gray-700"><b>{t('ملاحظات:', 'Notes:', 'Notes:')}</b> {c.medical_form.additional_notes}</p>
                        )}
                        {c.recording_url && (
                          <RecordingPlayer callId={c._id} durationSeconds={c.recording_duration_seconds} />
                        )}
                      </Card>
                    ))
                  )}
                </TabsContent>

                {/* Appointments */}
                <TabsContent value="appointments" className="space-y-3 mt-3">
                  {bundle.appointments.length === 0 ? (
                    <Card className="p-6 text-center text-sm text-gray-500">{t('لا توجد مواعيد', 'No appointments', 'Aucun RDV')}</Card>
                  ) : (
                    bundle.appointments.map((a) => (
                      <Card key={a._id} className="p-4" data-testid={`patient-appointment-${a._id}`}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-semibold text-[#0d47a1]">{a.doctor_name || '—'}</span>
                          <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{a.status}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{a.appointment_date} · {a.time_slot}</p>
                        {a.doctor_specialty && <p className="text-xs text-gray-400">{a.doctor_specialty}</p>}
                      </Card>
                    ))
                  )}
                </TabsContent>

                {/* Files */}
                <TabsContent value="files" className="space-y-3 mt-3">
                  {bundle.files.length === 0 ? (
                    <Card className="p-6 text-center text-sm text-gray-500">{t('لا توجد ملفات', 'No files', 'Aucun fichier')}</Card>
                  ) : (
                    bundle.files.map((f) => (
                      <Card key={f._id} className="p-3 flex items-center gap-3" data-testid={`patient-file-${f._id}`}>
                        <Paperclip size={18} className="text-[#1565c0] flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-[#0d47a1] truncate">{f.original_filename || f._id}</p>
                          <p className="text-xs text-gray-500">{f.description || f.content_type || ''}</p>
                          <p className="text-xs text-gray-400">{safeDate(f.uploaded_at || f.created_at)}</p>
                        </div>
                        {f._id && (
                          <a
                            href={`${API}/files/${f._id}/download`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs text-[#1565c0] hover:underline"
                            data-testid={`patient-file-download-${f._id}`}
                          >
                            {t('تحميل', 'Download', 'Télécharger')}
                          </a>
                        )}
                      </Card>
                    ))
                  )}
                </TabsContent>
              </Tabs>

              <div className="pt-2">
                <Button variant="outline" onClick={() => setSelectedId(null)} className="w-full">
                  {t('إغلاق', 'Close', 'Fermer')}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminPatientsView;
